import { PlusIcon, Trash2Icon } from 'lucide-react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import { TeaserHostForm } from './types';

type Props = { pageIndex: number; sectionIndex: number; readOnly?: boolean };

/**
 * The ordered CSS selectors that locate one section on the customer's page.
 *
 * Not behind its own disclosure any more — the whole advanced area it lives in
 * is one, and nesting a second inside it made the escape hatch two clicks deep.
 * What it shows is still the *whole* list: the weaker entries are what carry a
 * section through a customer redeploy, so the UI must never quietly drop them.
 */
const SectionSelectors = ({ pageIndex, sectionIndex, readOnly }: Props) => {
  const { t } = useTranslation();
  const { control } = useFormContext<TeaserHostForm>();
  const { fields, append, remove } = useFieldArray({
    control,
    name: `teaser.pages.${pageIndex}.sections.${sectionIndex}.match`,
  });

  return (
    <div className="flex flex-col gap-2">
      <Label className="text-sm leading-5 font-medium">
        {t('integrations.web_teaser.sections.selectors')}
      </Label>
      <p className="text-sm leading-5 text-muted-foreground">
        {t('integrations.web_teaser.sections.selectors_help')}
      </p>

      {fields.map((selector, index) => (
        <div key={selector.id} className="flex items-center gap-2">
          <Controller
            control={control}
            name={`teaser.pages.${pageIndex}.sections.${sectionIndex}.match.${index}.value`}
            render={({ field }) => (
              <Input
                className="font-mono"
                placeholder={t('integrations.web_teaser.sections.selector_placeholder')}
                readOnly={readOnly}
                {...field}
                value={field.value ?? ''}
              />
            )}
          />
          {!readOnly && (
            <Button
              className="hover:text-destructive hover:[&_svg]:text-destructive"
              type="button"
              variant="ghost-neutral"
              size="icon-sm"
              aria-label={t('common.delete')}
              onClick={() => remove(index)}
            >
              <Trash2Icon size={16} />
            </Button>
          )}
        </div>
      ))}

      {!readOnly && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="self-start"
          onClick={() => append({ value: '' })}
        >
          <PlusIcon size={16} />
          {t('integrations.web_teaser.sections.add_selector')}
        </Button>
      )}
    </div>
  );
};

export default SectionSelectors;
